import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { cn } from '@/shared/lib/utils';
import { InteractionType } from '@momentum/types';
import { StatusConfig } from '../hooks/useGoalStatus';
import { GoalCardHeader } from './GoalCardHeader';
import { GoalCardActions } from './GoalCardActions';
import { GoalKPIs } from './GoalKPIs';
import { GoalCardDates } from './GoalCardDates';
import { FullIssueWithChildren } from '@/shared/utils/issue-guards';

interface GoalCardContentProps {
  goal: FullIssueWithChildren;
  currentEmployeeId: string;
  hasChildren: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  statusConfig: StatusConfig;
  interactionCount: number;
  descendantInteractionCount: number;
  blockagesCount: number;
  onShowInteractions: (types: InteractionType[]) => void;
  onShowView: () => void;
  isHovered: boolean;
  level: number;
}

export const GoalCardContent: React.FC<GoalCardContentProps> = ({
  goal,
  currentEmployeeId,
  hasChildren,
  isExpanded,
  onToggle,
  statusConfig,
  interactionCount,
  descendantInteractionCount,
  blockagesCount,
  onShowInteractions,
  onShowView,
  isHovered,
  level,
}) => {
  return (
    <div
      className={cn(
        'group relative bg-white rounded-sm border border-gray-200 shadow-sm overflow-hidden',
        'transition-all duration-200 mb-3 cursor-pointer relative',
        isHovered && 'shadow-md border-gray-300',
        level === 0 && 'shadow-md'
      )}
      onClick={onShowView}
    >
      <div
        className={cn(
          'absolute start-0 inset-y-0 w-1 transition-all',
          statusConfig.barColor
        )}
      />

      <div className="p-5 ps-4 relative">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2">
            {hasChildren && (
              <div onClick={(e) => e.stopPropagation()} className="pt-2.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggle}
                  className="flex-shrink-0 h-5 w-5 p-0 text-gray-500"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4 rtl:rotate-180" />
                  )}
                </Button>
              </div>
            )}
            <GoalCardHeader
              title={goal.title}
              description={goal.description}
              statusConfig={statusConfig}
              creator={goal.issueCreator}
              visibility={goal.visibility}
              onShowView={onShowView}
            />
          </div>

          <div
            className="flex items-center gap-2"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <GoalCardActions
              goal={goal}
              currentEmployeeId={currentEmployeeId}
              interactionCount={interactionCount}
              descendantInteractionCount={descendantInteractionCount}
              blockagesCount={blockagesCount}
              onShowInteractions={onShowInteractions}
            />
          </div>
        </div>

        <GoalCardDates startDate={goal.startDate} dueDate={goal.dueDate} />

        <GoalKPIs
          goalId={goal._id}
          keyResults={goal.keyResults}
          status={goal.status}
          onKPIClick={onShowView}
          currentEmployeeId={currentEmployeeId}
          goal={goal}
        />
      </div>
    </div>
  );
};
